import { useState, useEffect } from "react";
import { Link, useLoaderData, useNavigate, useSubmit } from "react-router-dom";
import { useAuthStore, User } from "src/components/main/authStore";
import {
    round, weekDays, numberReducer, simpleStatusColorCode,
    MyDate, reportWriterIds,
    OfficesLocationMap,
    calculateDistance,
} from "src/components/common/functions";
import { getDatefromWeekNum, getDevice } from "./types";
import {
    DetailLoaderData, InOut, JobTagForm,
} from "./types";
import MapRange from "./mapRange";
import { LatLngTuple } from "leaflet";
export default function AllTimeCardsDetail(){
    const { data: initialData, reportsList, week } = useLoaderData() as DetailLoaderData;
    const navigate = useNavigate();
    const submit = useSubmit();
    const user = useAuthStore((state) => state.user) as User;

    const [data, setData] = useState<JobTagForm>(initialData);
    const [isEdittingTime, setIsEdittingTime] = useState(false);
    const [comments, setComments] = useState(initialData.comments ?? "");

    const [mapRangeKey, setMapRangeKey] = useState("");
    const locationToAddress = (locationArr: [number, number, number]) => {
        if(!locationArr) return;
        for(let [name, [lat, lng]] of Object.entries(OfficesLocationMap)){
            if(calculateDistance(lat, lng, locationArr) < locationArr[2] / 1609.34){
                return name;
            }
        }
        if(locationArr[2]){
            return `(${round(locationArr[0], 3)}, ${round(locationArr[1], 3)}), ${(locationArr[2] / 1609.34).toFixed(2)} miles`;
        }else{
            return `${round(locationArr[0], 3)}, ${round(locationArr[1], 3)}`;
        }
    }
    useEffect(() => {
        setData(initialData);
        setComments(initialData.comments ?? "");
        setIsEdittingTime(false);
    }, [initialData]);
    // ── helpers ──────────────────────────────────────────────
    const updateDayInOut = (
        dayIndex: number,
        ioIndex: number,
        updater: (io: InOut) => InOut
    ): void => setData(prev => ({
        ...prev,
        form: prev.form.map((day, dIdx) =>
            dIdx === dayIndex
                ? { ...day, inOut: day.inOut.map((io, iIdx) => iIdx === ioIndex ? updater(io) : io) }
                : day
        ),
    }));
    // ── handlers ─────────────────────────────────────────────
    const handleSaveTime = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        const fd = new FormData();
        fd.append("week", week);
        fd.append("employeeId", data.userId);
        fd.append("data", JSON.stringify(data));
        submit(fd, { method: "post", action: "UpdateTime" });
        setIsEdittingTime(false);
    };
    const handleSubmitWeek = (): void => {
        if (!window.confirm("Are you sure to submit for the week?")) return;
        const fd = new FormData();
        fd.append("week", week);
        fd.append("employeeId", data.userId);
        submit(fd, { method: "post", action: "Submit" });
    };
    const handleApproval = (decision: "Approved" | "Rejected"): void => {
        if(decision === "Rejected" && !decision){
            window.alert("Comments are required for rejection.");
            return;
        }
        const miniMap = { "Approved": "approve", "Rejected": "reject"};
        if(!window.confirm(`Are you sure to ${miniMap[decision]}?`)) return;
        const fd = new FormData();
        fd.append("week", week);
        fd.append("employeeId", data.userId);
        fd.append("decision", decision);
        fd.append("comments", comments);
        submit(fd, { method: "post", action: "Approval" });
    };
    const handleDownload = async () => {
        if (!data.pdfId) { window.alert("File Error."); return; }
        const formData = new FormData();
            formData.append("action", "downloadFile");
            formData.append("fileId", data.pdfId);
            formData.append("fileName", `Time Card ${data.userName} ${week}`);
            submit(formData, {method: "post", action: "Download" });
    };
    return (
        <div className="cssContent pb-3">
            <div className="mt-3 mb-4 d-flex align-items-center gap-3 flex-wrap">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(`/AllTimeCards/${week}`)}
                >
                    ← Back
                </button>
                <h4 className="fw-bold">Week: {week}</h4>
                <h4 className="fw-bold">{data.userName}</h4>
                <h4>
                    <span className="me-2">Status:</span>
                    <strong style={{ color: simpleStatusColorCode[data.status] }}>
                        {data.status}
                    </strong>
                </h4>
                {data.comments && (
                    <h4 className="mb-0">
                        <span className="me-2">Comments:</span>
                        <strong className="d-block cssNotes" style={{ color: simpleStatusColorCode[data.status] }}>
                            {data.comments}
                        </strong>
                    </h4>
                )}
                {data.pdfId && (
                    <button type="button" className="btn btn-primary" onClick={handleDownload}>
                        Download
                    </button>
                )}
            </div>
            <form className="border rounded p-2" onSubmit={handleSaveTime}>
                <div className="row">
                    {weekDays.map((renderedDay, renderedDayIndex) => (
                        <div className="col-lg" key={renderedDayIndex}>
                            <div className="h4 text-secondary">
                                {renderedDay} ({getDatefromWeekNum(week, renderedDayIndex)})
                            </div>
                            {isEdittingTime && (
                                <span className="btn btn-success m-1" onClick={() =>
                                    setData(prev => ({
                                        ...prev,
                                        form: prev.form.map((day, dIdx) =>
                                            dIdx === renderedDayIndex
                                                ? { ...day, inOut: [{ m: true, in: "", out: "" }, ...day.inOut] }
                                                : day
                                        ),
                                    }))
                                }>Add To Below</span>
                            )}
                            {data.form[renderedDayIndex].inOut.map((renderedIo, renderedIoIndex) => (
                                <div key={renderedIoIndex} className="p-2 mt-2 border rounded">
                                    <div>
                                        <span className={"fw-bold" + (renderedIo.m ? " text-danger" : "")}>In</span>:{" "}
                                        <span className={renderedIo.modifiedIn ? "text-decoration-line-through" : ""}>{renderedIo.in}</span>
                                        {!isEdittingTime && renderedIo.modifiedIn && (
                                            <>
                                                <span className="text-danger ms-2">{(new MyDate("2021/05/21 " + renderedIo.modifiedIn)).ss()}</span>
                                                {renderedIo.modifiedInNotes && <div className="text-danger">{renderedIo.modifiedInNotes}</div>}
                                            </>
                                        )}
                                        {renderedIo.inLocation && (
                                            <div className="mt-2">
                                                <span className={renderedIo.m ? "text-danger" : ""}>
                                                    <span className="fw-bold">Location:</span>
                                                    <span
                                                        className = "pointer text-primary ms-2"
                                                        onClick = {() => {
                                                            setMapRangeKey(data.userId + ","+ week + "," + renderedDayIndex + "," + renderedIoIndex + ",in");
                                                        }}
                                                    >
                                                        {locationToAddress(renderedIo.inLocation)}
                                                    </span>
                                                    {(data.userId + ","+ week + "," + renderedDayIndex + "," + renderedIoIndex + ",in") === mapRangeKey?
                                                    <MapRange
                                                        closeIt = {() => {setMapRangeKey("")}}
                                                        coords = {renderedIo.inLocation.slice(0, 2) as LatLngTuple}
                                                        accuracy = {renderedIo.inLocation[2]} 
                                                    />
                                                    :<></>}
                                                    {/*
                                                    <a className="ms-2"
                                                        href={`https://www.google.com/maps/search/${renderedIo.inLocation.slice(0, 2).join(",")}`}
                                                        target="_blank" rel="noreferrer"
                                                    >
                                                        see on google map
                                                    </a>
                                                    */}
                                                </span>
                                            </div>
                                        )}
                                        {renderedIo.inUserAgent && (
                                            <div className="mt-2">
                                                <span className={renderedIo.m ? "text-danger" : ""}>
                                                    <span className="fw-bold me-2">Device:</span>
                                                    {getDevice(renderedIo.inUserAgent)}
                                                </span>
                                            </div>
                                        )}
                                        {isEdittingTime && (
                                            <>
                                                <input
                                                    type="time" step="1"
                                                    className="form-control text-danger mt-1"
                                                    value={renderedIo.modifiedIn ?? ""}
                                                    min={renderedIoIndex > 0
                                                        ? data.form[renderedDayIndex].inOut[renderedIoIndex - 1].modifiedOut ||
                                                        new Date("2021/05/21 " + data.form[renderedDayIndex].inOut[renderedIoIndex - 1].out).toLocaleTimeString("en-US", { hour12: false }) || ""
                                                        : undefined}
                                                    max={renderedIo.modifiedOut || renderedIo.out}
                                                    onChange={e => updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({ ...io, modifiedIn: e.target.value }))}
                                                />
                                                {renderedIo.modifiedIn && (
                                                    <textarea
                                                        className="form-control text-danger mt-1"
                                                        placeholder="Reason for modification..."
                                                        value={renderedIo.modifiedInNotes ?? ""}
                                                        onChange={e => updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({ ...io, modifiedInNotes: e.target.value }))}
                                                    />
                                                )}
                                                <span className="btn btn-warning btn-sm mt-1"
                                                    onClick={() => updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({ ...io, modifiedIn: "" }))}
                                                >Clear</span>
                                            </>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center mt-2">
                                        <label htmlFor={`${renderedDayIndex} ${renderedIoIndex} break`} className="fw-bold text-nowrap mb-0">
                                            10-min break {renderedIoIndex + 1}:
                                        </label>
                                        <span className={`fw-bold text-${renderedIo.break ? "info" : "danger"} ms-2`}>
                                            {renderedIo.break ? "yes" : "no"}
                                        </span>
                                    </div>
                                    <div className="mt-2">
                                        <span className={"fw-bold" + (renderedIo.m ? " text-danger" : "")}>Out</span>:{" "}
                                        <span className={renderedIo.modifiedOut ? "text-decoration-line-through" : ""}>{renderedIo.out}</span>
                                        {!isEdittingTime && renderedIo.modifiedOut && (
                                            <>
                                                <span className="text-danger ms-2">{(new MyDate("2021/05/21 " + renderedIo.modifiedOut)).ss()}</span>
                                                {renderedIo.modifiedOutNotes && <div className="text-danger">{renderedIo.modifiedOutNotes}</div>}
                                            </>
                                        )}
                                        {renderedIo.outLocation && (
                                            <div className="mt-2">
                                                <span className={renderedIo.m ? "text-danger" : ""}>
                                                    <span className="fw-bold">Location:</span>
                                                    <span
                                                        className = "pointer text-primary ms-2"
                                                        onClick = {() => {
                                                            setMapRangeKey(data.userId + ","+ week + "," + renderedDayIndex + "," + renderedIoIndex + ",out");
                                                        }}
                                                    >
                                                        {locationToAddress(renderedIo.outLocation)}
                                                    </span>
                                                    {(data.userId + ","+ week + "," + renderedDayIndex + "," + renderedIoIndex + ",out") === mapRangeKey?
                                                    <MapRange
                                                        closeIt = {() => {setMapRangeKey("")}}
                                                        coords = {renderedIo.outLocation.slice(0, 2) as LatLngTuple}
                                                        accuracy = {renderedIo.outLocation[2]} 
                                                    />
                                                    :<></>}
                                                    {/*
                                                    <a className="ms-2"
                                                        href={`https://www.google.com/maps/search/${renderedIo.outLocation.slice(0, 2).join(",")}`}
                                                        target="_blank" rel="noreferrer"
                                                    >
                                                        see on google map
                                                    </a>
                                                    */}
                                                </span>
                                            </div>
                                        )}
                                        {renderedIo.outUserAgent && (
                                            <div className="mt-2">
                                                <span className={renderedIo.m ? "text-danger" : ""}>
                                                    <span className="fw-bold me-2">Device:</span>
                                                    {getDevice(renderedIo.outUserAgent)}
                                                </span>
                                            </div>
                                        )}
                                        {isEdittingTime && (
                                            <>
                                                <input
                                                    type="time" step="1"
                                                    className="form-control text-danger ms-2 mt-1"
                                                    value={renderedIo.modifiedOut ?? ""}
                                                    min={renderedIo.modifiedIn || new Date("2021/05/21 " + renderedIo.in).toLocaleTimeString("en-US", { hour12: false }) || ""}
                                                    onChange={e => updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({ ...io, modifiedOut: e.currentTarget.value }))}
                                                />
                                                {renderedIo.modifiedOut && (
                                                    <textarea
                                                        className="form-control text-danger mt-1"
                                                        placeholder="Reason for modification..."
                                                        value={renderedIo.modifiedOutNotes ?? ""}
                                                        onChange={e => updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({ ...io, modifiedOutNotes: e.currentTarget.value }))}
                                                    />
                                                )}
                                                <span className="btn btn-warning btn-sm mt-1"
                                                    onClick={() => updateDayInOut(renderedDayIndex, renderedIoIndex, io => ({ ...io, modifiedOut: "" }))}
                                                >Clear</span>
                                            </>
                                        )}
                                    </div>
                                    {(renderedIo.duration === 0 || renderedIo.duration != null) && !isEdittingTime && (
                                        <div className="mt-2">
                                            <span className={renderedIo.m ? "text-danger" : ""}>
                                                <strong>Duration</strong>: {renderedIo.duration} h
                                            </span>
                                        </div>
                                    )}
                                    <div className="mt-2">
                                        <label
                                            htmlFor={`${renderedDayIndex} ${renderedIoIndex} notes`}
                                            className={"fw-bold" + (renderedIo.m ? " text-danger" : "") + " mb-0"}
                                        >Notes:</label>
                                        <div className={"d-block cssNotes" + (renderedIo.m ? " text-danger" : "")}>{renderedIo.notes}</div>
                                    </div>
                                    {reportWriterIds.includes(user.id) && renderedIo.reports?.length ? (
                                        <div className="mt-2">
                                            {renderedIo.reports.map((report, ri) => (
                                                <li key={ri}>
                                                    {report.start} ~ {report.end}{" "}
                                                    <Link to={`/Reports/${report.id}`}>
                                                        {reportsList.find(item => item.value === report.id)?.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </div>
                                    ) : null}
                                    {isEdittingTime && (
                                        <div className="m-1 mt-2">
                                            {renderedIo.m && (
                                                <span className="btn btn-danger m-1" onClick={() => {
                                                    if (!window.confirm("Are you sure to delete this?")) return;
                                                    setData(prev => ({
                                                        ...prev,
                                                        form: prev.form.map((day, dIdx) =>
                                                            dIdx === renderedDayIndex
                                                                ? { ...day, inOut: [...day.inOut.slice(0, renderedIoIndex), ...day.inOut.slice(renderedIoIndex + 1)] }
                                                                : day
                                                        ),
                                                    }));
                                                }}>Delete This</span>
                                            )}
                                            <span className="btn btn-success m-1" onClick={() =>
                                                setData(prev => ({
                                                    ...prev,
                                                    form: prev.form.map((day, dIdx) =>
                                                        dIdx === renderedDayIndex
                                                            ? { ...day, inOut: [...day.inOut.slice(0, renderedIoIndex + 1), { m: true, in: "", out: "" }, ...day.inOut.slice(renderedIoIndex + 1)] }
                                                            : day
                                                    ),
                                                }))
                                            }>Add To Below</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                            <div className="mt-2 d-block">
                                <label htmlFor={`${renderedDayIndex} summary`} className="fw-bold mb-0">Summary:</label>
                                <div className="cssNotes d-block">{data.form[renderedDayIndex].notes}</div>
                            </div>
                            <div className="mt-2">
                                <span className="fw-bold me-2">Total:</span>
                                {data.form[renderedDayIndex].inOut.length
                                    ? data.form[renderedDayIndex].inOut
                                        .map(io => io.duration ?? 0)
                                        .map(v => round(v, 2))
                                        .reduce(numberReducer, 0)
                                        .toFixed(2)
                                    : null}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="fw-bold h5 mt-2">
                    Week Total:{" "}
                    {data.form
                        .map(day => day.inOut.length ? day.inOut.map(io => io.duration ?? 0).reduce(numberReducer, 0) : 0)
                        .reduce(numberReducer, 0)
                        .toFixed(2)}
                </div>
                <div className="d-flex gap-2 flex-wrap mt-3">
                    {isEdittingTime
                        ? 
                        <button
                            children = "Save Time"
                            type = "submit"
                            className = "btn btn-warning"
                        />
                        : 
                        <button
                            children = "Edit Time"
                            type = "button"
                            className = "btn btn-warning"
                            onClick = {e => {
                                e.preventDefault();
                                setIsEdittingTime(true);
                            }}
                        />
                    }
                    {data.status === "Created" && !isEdittingTime && (
                        <span className="btn btn-primary" onClick={handleSubmitWeek}>
                            Submit For the Week
                        </span>
                    )}
                </div>
                {data.status === "Submitted"  && (
                    <div className="mt-3">
                        <label className="fw-bold d-block mb-1">Approver Comments:</label>
                        <textarea
                            className="form-control mb-2"
                            style={{ maxWidth: 500 }}
                            value={comments}
                            onChange={e => setComments(e.currentTarget.value)}
                        />
                        <div className="d-flex gap-2">
                            <span className="btn btn-success" onClick={() => handleApproval("Approved")}>Approve</span>
                            <span className="btn btn-danger" onClick={() => handleApproval("Rejected")}>Reject</span>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
}